// WebSocket signaling client with automatic reconnect.
//
// One instance is shared by the app: it authenticates with the Go server,
// emits typed events, and forwards offer/answer/ice frames to listeners.
import type { SignalingMessage } from './protocol';

export type SignalingStatus = 'idle' | 'connecting' | 'open' | 'closed';

interface SignalingEvents {
  onMessage: (msg: SignalingMessage) => void;
  onStatus: (status: SignalingStatus) => void;
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15000;

export class SignalingClient {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private stopped = false;
  private reconnectTimer: number | null = null;

  constructor(
    private readonly serverUrl: string,
    private readonly token: string,
    private readonly events: SignalingEvents
  ) {
    // Mobile browsers freeze timers in backgrounded tabs, so a backoff timer
    // can stall for minutes after the tab is restored. When the app becomes
    // visible again, reconnect immediately instead of waiting for the timer.
    const resumeReconnect = () => {
      if (this.stopped) return;
      if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.attempts = 0;
      this.connect();
    };
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      resumeReconnect();
    });
    window.addEventListener('online', resumeReconnect);
  }

  /** Opens the socket; resolves once the server accepts the upgrade. */
  connect(): void {
    this.stopped = false;
    this.events.onStatus('connecting');
    const url = `${this.serverUrl}?token=${encodeURIComponent(this.token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempts = 0;
      this.events.onStatus('open');
      // Declare our role so the server can report presence correctly.
      this.send({ type: 'hello', role: 'viewer' });
    };

    ws.onmessage = (ev: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(ev.data) as SignalingMessage;
        this.events.onMessage(msg);
      } catch {
        // Ignore malformed frames rather than tearing down the socket.
      }
    };

    ws.onclose = () => {
      this.events.onStatus('closed');
      this.ws = null;
      if (!this.stopped) this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose always follows; reconnect handled there.
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** this.attempts,
      RECONNECT_MAX_MS
    );
    this.attempts += 1;
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  /** Sends a signaling frame; returns false when the socket is not open. */
  send(msg: SignalingMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  /** Stops reconnecting and closes the socket (logout / unmount). */
  disconnect(): void {
    this.stopped = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.events.onStatus('closed');
  }
}
